import styled, { css } from 'styled-components';

import { ReactComponent as ChevronDownIcon } from 'icons/chevron-down.svg';
import {
  borderDangerBase,
  helpTextDanger,
  helpTextDefault,
  inputBorderRest,
} from 'styles/variables';
import { mediaBreakpointDown } from 'styles/grid';

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  flex: 1;
  position: relative;
  height: 52px;
`;

interface SelectContainerProps {
  hasError?: boolean;
  isCustom?: boolean;
}

export const SelectContainer: React.FC<SelectContainerProps> =
  styled.button<SelectContainerProps>`
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 4px;
    box-shadow: none;
    height: 100%;

    border: 2px solid
      ${({ hasError, theme }) =>
        hasError
          ? borderDangerBase
          : theme.configs.tertiaryBox.secondaryFontColor};
    &::placeholder {
      color: ${({ theme }) => theme.configs.tertiaryBox.mainFontColor};
    }

    ${mediaBreakpointDown.sm} {
      border: 3px solid
        ${({ theme, isCustom }) => {
          if (isCustom) return theme.configs.CTAButton.bgColor;
          return theme.configs.tertiaryBox.secondaryFontColor;
        }};
    }

    background-color: ${({ theme }) => theme.configs.tertiaryBox.bgColor};
    font-family: inherit;
  ` as any;

export const SelectWrapper = styled.div<{ spaceBetween?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ spaceBetween }) =>
    spaceBetween ? 'space-between' : 'center'};
  line-height: 1.5;
  width: 100%;
  gap: 4px;
`;

export const SelectLabel = styled.label`
  align-items: center;
  display: grid;
  flex-wrap: wrap;
  overflow: hidden;
  box-sizing: border-box;
  color: ${({ theme }) => theme.configs.CTAButton.bgColor};
  margin-bottom: 0px;
`;

export const SelectValue = styled.span`
  flex-wrap: wrap;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
`;

export const SelectToggleButton = styled.div<{
  disabled?: boolean;
  isMenuOpen?: boolean;
}>`
  align-items: center;
  align-self: stretch;
  display: flex;
  flex-shrink: 0;
  box-sizing: border-box;

  background-color: transparent;

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.6;
    `}

  svg {
    transition: all 0.3s;
    transform: ${({ isMenuOpen }) =>
      isMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  }
`;

export const StyledChevronIcon = styled(ChevronDownIcon)`
  color: ${({ theme }) => theme.configs.CTAButton.bgColor};

  /* Target specific SVG elements */
  path {
    fill: currentColor;
  }

  /* Add hover effect */
  &:hover {
    color: ${({ theme }) => theme.configs.CTAButton.bgColor};
  }
`;

export const SelectMenu = styled.ul<{ isOpen?: boolean }>`
  all: unset;
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  background-color: ${({ theme }) => theme.configs.tertiaryBox.bgColor};
  border: 1px solid ${inputBorderRest};
  border-radius: 4px;
  box-shadow: 0 0 10px 0
    ${({ theme }) => theme.configs.tertiaryBox.secondaryFontColor};
  max-height: 200px;
  overflow-y: auto;
  width: 100%;
  ${({ isOpen }) =>
    !isOpen &&
    css`
      display: none;
    `}
`;

export const SelectItem = styled.li<{ isHighlighted?: boolean }>`
  font-size: 14px;
  line-height: 1.5;
  cursor: pointer;

  ${({ isHighlighted }) =>
    isHighlighted &&
    css`
      background-color: #e5f1ff;
    `}
`;

export const SelectNoResults = styled.div`
  display: block;
  padding: 12px 16px;
  font-size: 14px;
  color: ${({ theme }) => theme.configs.tertiaryBox.secondaryFontColor};
  text-align: center;
`;

export const SelectHelperText = styled.div<{
  isError?: boolean;
  size?: 'sm' | 'lg' | 'md';
}>`
  align-items: center;
  align-self: stretch;
  display: flex;
  flex-shrink: 0;
  box-sizing: border-box;
  padding: 2px 6px;
  color: ${({ isError }) => {
    if (isError) {
      return helpTextDanger;
    }

    return helpTextDefault;
  }};

  ${({ size }): string => {
    switch (size) {
      case 'sm':
        return `
          font-size: 10px;
          line-height: 15px;
        `;
      case 'lg':
        return `
          font-size: 16px;
          line-height: 21px;
        `;
      case 'md':
      default:
        return `
          font-size: 14px;
          line-height: 1.5;
        `;
    }
  }}
`;
